import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table,
    TableHeader,
    TableBody,
    TableColumn,
    TableRow,
    TableCell,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Tooltip,
} from "@heroui/react";
import { useState, useMemo, useEffect } from "react";
import { useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { ThemedText } from "@/components/ui";
import {
    LuCheck,
    LuX,
    LuTrash2,
    LuArrowRightLeft,
    LuArrowDown,
    LuZap,
    LuPlus,
    LuInfo,
} from "react-icons/lu";

const UNITS = [
    { key: "bundles", label: "Ikat", multiplier: 300 },
    { key: "trays", label: "Papan", multiplier: 30 },
    { key: "pieces", label: "Butir", multiplier: 1 },
    { key: "broken", label: "Butir (Pecah)", multiplier: 1 },
];

const StockDistributionModal = ({
    isOpen,
    onClose,
    onConfirm,
    productionBreakdown: initialBreakdown = {
        bundles: 0,
        trays: 0,
        pieces: 0,
        broken: 0,
    },
    isSubmitting = false,
}) => {
    const { data: warehouses, isLoading } = useFetchData("select/warehouse");

    // Local breakdown state (can be modified by conversion)
    const [breakdown, setBreakdown] = useState(initialBreakdown);
    const [warehouseGroups, setWarehouseGroups] = useState([]);

    // Reset when modal opens with new data
    useMemo(() => {
        setBreakdown(initialBreakdown);
        setWarehouseGroups([]);
    }, [initialBreakdown]);

    // Calculate distributed per unit
    const distributedPerUnit = useMemo(() => {
        const result = { bundles: 0, trays: 0, pieces: 0, broken: 0 };
        warehouseGroups.forEach((group) => {
            Object.keys(result).forEach((key) => {
                result[key] += Number(group.quantities[key] || 0);
            });
        });
        return result;
    }, [warehouseGroups]);

    // Calculate remaining per unit
    const remainingPerUnit = useMemo(() => {
        return {
            bundles: breakdown.bundles - distributedPerUnit.bundles,
            trays: breakdown.trays - distributedPerUnit.trays,
            pieces: breakdown.pieces - distributedPerUnit.pieces,
            broken: breakdown.broken - distributedPerUnit.broken,
        };
    }, [breakdown, distributedPerUnit]);

    // Check if all distributed
    const isFullyDistributed = useMemo(() => {
        return (
            remainingPerUnit.bundles === 0 &&
            remainingPerUnit.trays === 0 &&
            remainingPerUnit.pieces === 0 &&
            remainingPerUnit.broken === 0
        );
    }, [remainingPerUnit]);

    // Filter only units with production
    const activeUnits = useMemo(() => {
        return UNITS.filter((u) => breakdown[u.key] > 0);
    }, [breakdown]);

    // Handle unit conversion
    const handleConvert = (fromUnitKey, toUnitKey) => {
        const fromUnit = UNITS.find((u) => u.key === fromUnitKey);
        const toUnit = UNITS.find((u) => u.key === toUnitKey);

        if (!fromUnit || !toUnit) return;

        const remaining = remainingPerUnit[fromUnitKey];
        if (remaining <= 0) {
            showToast(
                "warning",
                "Tidak Ada Sisa",
                `Tidak ada ${fromUnit.label} yang tersisa untuk dikonversi.`,
            );
            return;
        }

        const butirEquivalent = remaining * fromUnit.multiplier;
        const convertedQty = butirEquivalent / toUnit.multiplier;
        const roundedQty = Math.round(convertedQty * 100) / 100;

        setBreakdown((prev) => ({
            ...prev,
            [fromUnitKey]: prev[fromUnitKey] - remaining,
            [toUnitKey]: prev[toUnitKey] + roundedQty,
        }));

        showToast(
            "success",
            "Berhasil Konversi",
            `${remaining} ${fromUnit.label} → ${roundedQty} ${toUnit.label}`,
        );
    };

    // Input style
    const INPUT_CLASS =
        "focus:border-primary w-full rounded border border-zinc-300 bg-zinc-100 px-2 py-1 text-sm focus:outline-none dark:border-zinc-600 dark:bg-zinc-800";

    // Warehouse group handlers
    const handleAddGroup = () => {
        setWarehouseGroups((prev) => [
            ...prev,
            {
                warehouse_id: null,
                warehouse: "",
                quantities: { bundles: 0, trays: 0, pieces: 0, broken: 0 },
            },
        ]);
    };

    const handleSetWarehouse = (groupIndex, warehouseId) => {
        const wh = warehouses.find((w) => w.value == warehouseId);
        setWarehouseGroups((prev) =>
            prev.map((g, i) =>
                i === groupIndex
                    ? { ...g, warehouse_id: warehouseId, warehouse: wh?.title || "" }
                    : g,
            ),
        );
    };

    const handleSetQty = (groupIndex, unitKey, value) => {
        setWarehouseGroups((prev) =>
            prev.map((g, i) => {
                if (i !== groupIndex) return g;
                const currentVal = g.quantities[unitKey];
                const maxVal = remainingPerUnit[unitKey] + currentVal;
                const clamped = Math.max(0, Math.min(Number(value), maxVal));
                return {
                    ...g,
                    quantities: { ...g.quantities, [unitKey]: clamped },
                };
            }),
        );
    };

    const handleFillRemaining = (groupIndex, unitKey) => {
        setWarehouseGroups((prev) =>
            prev.map((g, i) => {
                if (i !== groupIndex) return g;
                const currentVal = g.quantities[unitKey];
                const remaining = remainingPerUnit[unitKey];
                return {
                    ...g,
                    quantities: {
                        ...g.quantities,
                        [unitKey]: currentVal + remaining,
                    },
                };
            }),
        );
    };

    const handleRemoveGroup = (groupIndex) => {
        setWarehouseGroups((prev) => prev.filter((_, i) => i !== groupIndex));
    };

    // Warehouses already selected (to prevent duplicate)
    const selectedWarehouseIds = warehouseGroups
        .map((g) => g.warehouse_id)
        .filter(Boolean);

    const handleConfirm = () => {
        // Validate each group has a warehouse
        const hasInvalidGroup = warehouseGroups.some((g) => !g.warehouse_id);
        if (hasInvalidGroup) {
            showToast(
                "error",
                "Input Tidak Lengkap",
                "Pastikan semua grup memiliki gudang yang dipilih.",
            );
            return;
        }

        if (!isFullyDistributed) {
            showToast(
                "error",
                "Belum Lengkap",
                "Semua produksi harus didistribusikan sebelum menyimpan.",
            );
            return;
        }

        // Flatten to same format as before
        const distributionData = warehouseGroups.flatMap((group) =>
            activeUnits
                .filter((unit) => group.quantities[unit.key] > 0)
                .map((unit) => ({
                    warehouse_id: group.warehouse_id,
                    quantity: group.quantities[unit.key],
                    unit: unit.label,
                })),
        );

        onConfirm(distributionData);
    };

    const handleClose = () => {
        setWarehouseGroups([]);
        setBreakdown(initialBreakdown);
        onClose();
    };

    // Get conversion options for a unit (all other units, excluding broken)
    const getConversionOptions = (unitKey) => {
        if (unitKey === "broken") return [];
        return UNITS.filter((u) => u.key !== unitKey && u.key !== "broken");
    };

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && handleClose()}
            placement="center"
            size="3xl"
            scrollBehavior="inside"
            isDismissable={!isSubmitting}
            isKeyboardDismissDisabled={isSubmitting}
            hideCloseButton={isSubmitting}
        >
            <ModalContent>
                <ModalHeader className="flex items-center gap-2">
                    <ThemedText size="md">Distribusi Stok Produksi</ThemedText>
                    <Tooltip
                        placement="bottom-start"
                        content={
                            <div className="px-1 py-2">
                                <ThemedText size="xs" className="font-semibold">
                                    Aturan Distribusi
                                </ThemedText>
                                <ul className="text-tiny mt-1 list-disc space-y-1 px-4">
                                    <li>
                                        Semua stok produksi wajib
                                        didistribusikan sampai habis.
                                    </li>
                                    <li>
                                        Tambah gudang tujuan, lalu isi jumlah
                                        per satuan.
                                    </li>
                                    <li>
                                        Gunakan tombol "↓" untuk mengisi
                                        otomatis sisa yang belum terdistribusi.
                                    </li>
                                    <li>
                                        Gunakan fitur "Konversi" di tabel atas
                                        jika perlu memecah satuan (misal: Ikat
                                        ke Papan).
                                    </li>
                                </ul>
                            </div>
                        }
                    >
                        <div className="text-default-400 hover:text-primary cursor-pointer">
                            <LuInfo size={16} />
                        </div>
                    </Tooltip>
                </ModalHeader>
                <ModalBody className="gap-5">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <ThemedText variant="secondary">
                                Memuat data gudang...
                            </ThemedText>
                        </div>
                    ) : (
                        <>
                            {/* Production Breakdown Table */}
                            <div>
                                <ThemedText
                                    size="sm"
                                    className="mb-2 font-semibold"
                                >
                                    Status Distribusi Produksi:
                                </ThemedText>
                                <Table
                                    aria-label="Status Distribusi"
                                    removeWrapper
                                >
                                    <TableHeader>
                                        <TableColumn className="w-12 text-center"></TableColumn>
                                        <TableColumn>Satuan</TableColumn>
                                        <TableColumn align="center">
                                            Produksi
                                        </TableColumn>
                                        <TableColumn align="center">
                                            Terdistribusi
                                        </TableColumn>
                                        <TableColumn align="center">
                                            Sisa
                                        </TableColumn>
                                        <TableColumn className="w-20 text-center">
                                            Aksi
                                        </TableColumn>
                                    </TableHeader>
                                    <TableBody>
                                        {activeUnits.map((unit) => {
                                            const production =
                                                breakdown[unit.key];
                                            const distributed =
                                                distributedPerUnit[unit.key];
                                            const remaining =
                                                remainingPerUnit[unit.key];
                                            const isDone = remaining === 0;
                                            const conversionOptions =
                                                getConversionOptions(unit.key);

                                            return (
                                                <TableRow key={unit.key}>
                                                    <TableCell className="text-center">
                                                        {isDone ? (
                                                            <LuCheck
                                                                className="text-success mx-auto"
                                                                size={18}
                                                            />
                                                        ) : (
                                                            <LuX
                                                                className="text-danger mx-auto"
                                                                size={18}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium">
                                                            {unit.label}
                                                        </span>
                                                        <span className="text-default-400 ml-2 text-xs">
                                                            (1 ={" "}
                                                            {unit.multiplier}{" "}
                                                            Butir)
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center font-medium">
                                                        {production}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span
                                                            className={
                                                                isDone
                                                                    ? "text-success font-medium"
                                                                    : ""
                                                            }
                                                        >
                                                            {distributed}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span
                                                            className={
                                                                remaining > 0
                                                                    ? "text-danger font-medium"
                                                                    : "text-success"
                                                            }
                                                        >
                                                            {remaining}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <Tooltip content="Konversi Satuan">
                                                            <div>
                                                                <Dropdown
                                                                    placement="bottom-end"
                                                                    isDisabled={
                                                                        remaining <=
                                                                        0
                                                                    }
                                                                >
                                                                    <DropdownTrigger>
                                                                        <Button
                                                                            isIconOnly
                                                                            size="sm"
                                                                            variant="light"
                                                                            isDisabled={
                                                                                remaining <=
                                                                                0
                                                                            }
                                                                        >
                                                                            <LuArrowRightLeft
                                                                                size={
                                                                                    14
                                                                                }
                                                                                className={
                                                                                    remaining <=
                                                                                    0
                                                                                        ? "text-default-300"
                                                                                        : "text-default-500"
                                                                                }
                                                                            />
                                                                        </Button>
                                                                    </DropdownTrigger>
                                                                    <DropdownMenu aria-label="Konversi Satuan">
                                                                        {conversionOptions.map(
                                                                            (
                                                                                toUnit,
                                                                            ) => (
                                                                                <DropdownItem
                                                                                    key={
                                                                                        toUnit.key
                                                                                    }
                                                                                    onPress={() =>
                                                                                        handleConvert(
                                                                                            unit.key,
                                                                                            toUnit.key,
                                                                                        )
                                                                                    }
                                                                                    startContent={
                                                                                        <LuArrowRightLeft
                                                                                            size={
                                                                                                14
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                >
                                                                                    Konversi
                                                                                    ke{" "}
                                                                                    {
                                                                                        toUnit.label
                                                                                    }{" "}
                                                                                    (
                                                                                    {Math.round(
                                                                                        ((remaining *
                                                                                            unit.multiplier) /
                                                                                            toUnit.multiplier) *
                                                                                            100,
                                                                                    ) /
                                                                                        100}

                                                                                    )
                                                                                </DropdownItem>
                                                                            ),
                                                                        )}
                                                                    </DropdownMenu>
                                                                </Dropdown>
                                                            </div>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Arrow Divider */}
                            <div className="text-default-500 flex justify-center">
                                <LuArrowDown size={16} />
                            </div>

                            {/* Distribution by Warehouse Groups */}
                            <div className="flex flex-col gap-3">
                                <ThemedText size="sm" className="font-semibold">
                                    Daftar Distribusi:
                                </ThemedText>

                                {warehouseGroups.map((group, groupIndex) => (
                                    <div
                                        key={groupIndex}
                                        className="border-default-200 rounded-lg border p-3"
                                    >
                                        {/* Warehouse header */}
                                        <div className="mb-3 flex items-center gap-2">
                                            <select
                                                className={`${INPUT_CLASS} flex-1`}
                                                value={group.warehouse_id || ""}
                                                onChange={(e) =>
                                                    handleSetWarehouse(
                                                        groupIndex,
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                <option value="" disabled>
                                                    Pilih Gudang
                                                </option>
                                                {(warehouses || []).map((w) => (
                                                    <option
                                                        key={w.value}
                                                        value={w.value}
                                                        disabled={
                                                            selectedWarehouseIds.includes(
                                                                String(w.value),
                                                            ) &&
                                                            String(w.value) !==
                                                                String(
                                                                    group.warehouse_id,
                                                                )
                                                        }
                                                    >
                                                        {w.title}
                                                    </option>
                                                ))}
                                            </select>
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                color="danger"
                                                variant="light"
                                                onPress={() =>
                                                    handleRemoveGroup(
                                                        groupIndex,
                                                    )
                                                }
                                            >
                                                <LuTrash2 size={14} />
                                            </Button>
                                        </div>

                                        {/* Unit fields grid */}
                                        <div className="grid grid-cols-2 gap-2">
                                            {activeUnits.map((unit) => {
                                                const qty =
                                                    group.quantities[
                                                        unit.key
                                                    ] || 0;
                                                const remaining =
                                                    remainingPerUnit[unit.key];
                                                const maxVal = remaining + qty;

                                                return (
                                                    <div
                                                        key={unit.key}
                                                        className="flex items-center gap-1.5"
                                                    >
                                                        <ThemedText
                                                            size="xs"
                                                            className="w-20 shrink-0 font-medium"
                                                        >
                                                            {unit.label}
                                                        </ThemedText>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={maxVal}
                                                            className={`${INPUT_CLASS} text-right`}
                                                            value={qty || ""}
                                                            onChange={(e) =>
                                                                handleSetQty(
                                                                    groupIndex,
                                                                    unit.key,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="0"
                                                        />
                                                        <Tooltip content={`Isi sisa otomatis (${remaining})`}>
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="light"
                                                                isDisabled={
                                                                    remaining <=
                                                                    0
                                                                }
                                                                onPress={() =>
                                                                    handleFillRemaining(
                                                                        groupIndex,
                                                                        unit.key,
                                                                    )
                                                                }
                                                                className="min-w-8"
                                                            >
                                                                <LuZap
                                                                    size={14}
                                                                    className={
                                                                        remaining <=
                                                                        0
                                                                            ? "text-default-300"
                                                                            : "text-warning"
                                                                    }
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}

                                {!isFullyDistributed && (
                                    <Button
                                        size="sm"
                                        variant="flat"
                                        color="primary"
                                        startContent={<LuPlus size={16} />}
                                        onPress={handleAddGroup}
                                        className="self-start"
                                    >
                                        Tambah Gudang
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="flat"
                        onPress={handleClose}
                        size="sm"
                        isDisabled={isSubmitting}
                    >
                        Batal
                    </Button>
                    <Button
                        color="primary"
                        onPress={handleConfirm}
                        size="sm"
                        isDisabled={!isFullyDistributed || isSubmitting || warehouseGroups.some((g) => !g.warehouse_id)}
                        isLoading={isSubmitting}
                    >
                        {isSubmitting
                            ? "Menyimpan..."
                            : "Konfirmasi Distribusi"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default StockDistributionModal;
